import SimpleModal from "app/components/Modals/SimpleModal";
import CreatableSelect from "react-select/creatable";
import { MultiValue } from "react-select";
import Button from "app/storybookComponents/Button";
import {
  faCirclePlus,
  faCircleXmark,
} from "@fortawesome/pro-regular-svg-icons";
import SimpleModalFooter from "app/components/Modals/SimpleModalFooter";
import { useEffect, useMemo, useState } from "react";
import { selectAllCompanyUsersById } from "app/containers/Global/slice";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import WarningModal from "app/storybookComponents/Modals/WarningModal";
import { getS, getUserName, isValidEmail } from "utils/helperFunctions";
import {
  selectExecutiveWeeklyCheckInCompanyCustomList,
  updateExecutiveWeeklyCheckInCompanyCustomList,
  updateWeeklyCheckInCompanySettings,
  selectUpdateWeeklyCheckInCompanySettingsStatus,
  selectUpdatingCompanyExecutiveWeeklyCheckInCustomListStatus,
  getExternalExecutiveReportRecipients,
  updateExternalExecutiveReportRecipients,
  selectExternalExecutiveReportRecipients,
  selectUpdatingExternalExecutiveReportRecipientsStatus,
} from "../slice";
import AvatarCircle from "app/components/AvatarCircle";
import { getMemberOption } from "app/components/GlobalNavbar/helpers";

interface Props {
  show: boolean;
  onHide: () => void;
  areAllAdminsSelected?: boolean;
  showConfirmModal?: boolean;
}

// Unified option type for both system users and external emails
interface RecipientOption {
  label: string;
  value: string; // String(userAccountId) for system users, email address for external
  name: string;
  isExternal?: boolean;
}

const ConfigureWeeklyExecutiveCheckinReportModal = ({
  show,
  onHide,
  areAllAdminsSelected,
}: Readonly<Props>) => {
  const dispatch = useAppDispatch();
  const usersById = useAppSelector(selectAllCompanyUsersById);
  const executiveWeeklyCheckInCompanyCustomList = useAppSelector(
    selectExecutiveWeeklyCheckInCompanyCustomList
  );
  const updateWeeklyCheckInCompanySettingsStatus = useAppSelector(
    selectUpdateWeeklyCheckInCompanySettingsStatus
  );
  const updatingCompanyExecutiveWeeklyCheckInCustomListStatus = useAppSelector(
    selectUpdatingCompanyExecutiveWeeklyCheckInCustomListStatus
  );
  const externalExecutiveReportRecipients = useAppSelector(
    selectExternalExecutiveReportRecipients
  );
  const updatingExternalExecutiveReportRecipientsStatus = useAppSelector(
    selectUpdatingExternalExecutiveReportRecipientsStatus
  );
  const [showAddAdminButton, setShowAddAdminButton] = useState(false);
  const [selectedUserAccountIds, setSelectedUserAccountIds] = useState<{
    [key: number]: true;
  }>({});
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [externalRecipients, setExternalRecipients] = useState<
    Array<{ emailAddress: string; recipientName: string | null }>
  >([]);
  const [selectInputValue, setSelectInputValue] = useState("");

  useEffect(() => {
    const newSelectedUserAccountIds: { [key: number]: true } = {};
    executiveWeeklyCheckInCompanyCustomList?.forEach((userAccountId) => {
      newSelectedUserAccountIds[userAccountId] = true;
    });
    setSelectedUserAccountIds(newSelectedUserAccountIds);
  }, [executiveWeeklyCheckInCompanyCustomList]);

  useEffect(() => {
    if (!Object.keys(selectedUserAccountIds).length) {
      return setShowAddAdminButton(true);
    }
  }, [selectedUserAccountIds]);

  useEffect(() => {
    if (!show) return;
    dispatch(getExternalExecutiveReportRecipients());
  }, [show, dispatch]);

  useEffect(() => {
    if (!externalExecutiveReportRecipients?.length) return;
    setExternalRecipients(
      externalExecutiveReportRecipients.map(
        ({ emailAddress, recipientName }) => ({
          emailAddress,
          recipientName,
        })
      )
    );
  }, [externalExecutiveReportRecipients]);

  const onAddAllAdmins = () => {
    setSelectedUserAccountIds((prev) => {
      const updatedSelectedUserAccountIds: { [key: number]: true } = {
        ...prev,
      };
      Object.entries(usersById).forEach(([userAccountId, userInfo]) => {
        if (userInfo.tmgRoleId !== 1) return;
        updatedSelectedUserAccountIds[Number(userAccountId)] = true;
      });
      return updatedSelectedUserAccountIds;
    });
    setShowAddAdminButton(false);
  };

  const onRemoveAllAdmins = () => {
    setSelectedUserAccountIds((prev) => {
      const updatedSelectedUserAccountIds: { [key: number]: true } = {
        ...prev,
      };
      Object.entries(usersById).forEach(([userAccountId, userInfo]) => {
        if (userInfo.tmgRoleId !== 1) return;
        delete updatedSelectedUserAccountIds[Number(userAccountId)];
      });
      return updatedSelectedUserAccountIds;
    });
    setShowAddAdminButton(true);
  };

  const onCreateOption = (inputValue: string) => {
    const trimmed = inputValue.trim();
    if (!isValidEmail(trimmed)) return;
    setExternalRecipients((prev) => [
      ...prev,
      { emailAddress: trimmed, recipientName: null },
    ]);
  };

  const getAddOrRemoveAdminButton = () => {
    if (showAddAdminButton) {
      return (
        <Button
          onClick={onAddAllAdmins}
          variant="secondary-blue"
          className="border-0"
        >
          <FontAwesomeIcon icon={faCirclePlus} className="me-2" />
          Add all admins
        </Button>
      );
    }
    return (
      <Button
        onClick={onRemoveAllAdmins}
        variant="secondary-gray"
        className="border-0"
      >
        <FontAwesomeIcon icon={faCircleXmark} className="me-2" />
        Remove all listed admins (*)
      </Button>
    );
  };

  const getDropdownOptions = (userAccountIds: string[]): RecipientOption[] => {
    const userOptions: RecipientOption[] = [];
    userAccountIds.map(Number).forEach((userAccountId) => {
      const user = usersById[userAccountId];
      if (!user) {
        return;
      }
      userOptions.push({
        label: user.emailAddress,
        value: String(user.userAccountId),
        name: `${user.firstName} ${user.lastName}`,
      });
    });
    return userOptions;
  };

  // Full options list — memoized so it's not rebuilt on every keystroke
  const allUserOptions = useMemo(
    () => getDropdownOptions(Object.keys(usersById)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [usersById]
  );

  // Pre-filtered and capped options driven by the controlled input value.
  // Passing these + filterOption={() => true} prevents react-select from
  // running its own filter loop over the full list on every keystroke.
  const filteredOptions = useMemo(() => {
    if (!selectInputValue) return [];
    const lower = selectInputValue.toLowerCase();
    const matches = allUserOptions.filter(
      (o) =>
        o.label.toLowerCase().includes(lower) ||
        o.name.toLowerCase().includes(lower)
    );
    return matches.slice(0, 50);
  }, [selectInputValue, allUserOptions]);

  const handleSelectChange = (values: MultiValue<RecipientOption>) => {
    const newUserAccountIds: { [key: number]: true } = {};
    const newExternalRecipients: Array<{
      emailAddress: string;
      recipientName: string | null;
    }> = [];

    values?.forEach((option: RecipientOption) => {
      if (option.isExternal) {
        newExternalRecipients.push({
          emailAddress: option.value,
          recipientName: null,
        });
      } else {
        newUserAccountIds[Number(option.value)] = true;
      }
    });

    setSelectedUserAccountIds(newUserAccountIds);
    setExternalRecipients(newExternalRecipients);
  };

  const formatOptionLabel = (option: RecipientOption) => {
    if (option.isExternal) {
      return <div>{option.label}</div>;
    }

    const userAccountId = Number(option.value);
    const asterisk = usersById[userAccountId]?.tmgRoleId === 1 ? " *" : "";

    if (selectedUserAccountIds[userAccountId]) {
      const user = usersById[userAccountId];
      const name = getUserName(user);
      return (
        <div>
          {name}
          {asterisk}
        </div>
      );
    }

    const avatarCircle = (
      <AvatarCircle
        name={`${option.label}`}
        userAccountId={userAccountId}
        size="small"
      />
    );

    return getMemberOption({
      label: `${option.label}${asterisk}`,
      avatarCircle,
    });
  };

  const onSubmit = async () => {
    if (areAllAdminsSelected) {
      await dispatch(
        updateWeeklyCheckInCompanySettings({
          hasWeeklyCheckInExecutiveReportEnabledForAllAdmins: true,
          hasWeeklyCheckInExecutiveReportEnabledForCustomList: false,
        })
      );
    } else {
      await dispatch(
        updateWeeklyCheckInCompanySettings({
          hasWeeklyCheckInExecutiveReportEnabledForAllAdmins: false,
          hasWeeklyCheckInExecutiveReportEnabledForCustomList: true,
        })
      );
      await Promise.all([
        dispatch(
          updateExecutiveWeeklyCheckInCompanyCustomList({
            userAccountIds: Object.keys(selectedUserAccountIds).map(Number),
          })
        ),
        dispatch(
          updateExternalExecutiveReportRecipients({
            externalRecipients: externalRecipients.map(
              ({ emailAddress, recipientName }) => ({
                emailAddress,
                ...(recipientName !== null && { recipientName }),
              })
            ),
          })
        ),
      ]);
    }
    setShowConfirmationModal(false);
    onHide();
  };

  const onWarningModalHide = () => {
    if (areAllAdminsSelected) {
      return onHide();
    }
    setShowConfirmationModal(false);
  };

  const warningModalWarningTitle = useMemo(() => {
    if (!areAllAdminsSelected) {
      const internalCount = Object.keys(selectedUserAccountIds).length;
      const externalCount = externalRecipients.length;
      const totalLength = internalCount + externalCount;
      return `Enable Executive Check-in Report for ${totalLength} recipient${getS(
        totalLength
      )}?`;
    }

    const adminUserAccountIds = Object.values(usersById).filter(
      (user) => user.tmgRoleId === 1
    );
    return `Enable Executive Check-in Report for ${
      adminUserAccountIds.length
    } admin${getS(adminUserAccountIds.length)}?`;
  }, [
    selectedUserAccountIds,
    externalRecipients,
    areAllAdminsSelected,
    usersById,
  ]);

  const isWarningModalOpen = !!(areAllAdminsSelected || showConfirmationModal);
  const isLoading =
    updateWeeklyCheckInCompanySettingsStatus === "loading" ||
    updatingCompanyExecutiveWeeklyCheckInCustomListStatus === "loading" ||
    updatingExternalExecutiveReportRecipientsStatus === "loading";

  return (
    <>
      <WarningModal
        isOpen={show && isWarningModalOpen}
        hideModal={onWarningModalHide}
        modalTitle="Enable Executive Check-in Report"
        warningTitle={warningModalWarningTitle}
        warningMessage="An email report will be sent at the start of each check-in period with organization wide insights, themes, and sentiments. This report also includes department level updates, a spotlight for successes, and people to connect with for the period."
        onConfirmClick={onSubmit}
        isDanger={false}
        customButtonText="Confirm and enable"
        isInProgress={isLoading}
      />
      <SimpleModal
        show={show && !isWarningModalOpen}
        onHide={onHide}
        title="Enable Executive Check-in Report"
      >
        <p>
          Executives and external stakeholders will receive an email each cycle
          with organization wide insights, themes, and sentiments. This report
          also includes department level updates, a spotlight for successes, and
          people to connect with. You can add system users by name
          or email, or type any external email address directly.
        </p>
        <hr className="m-0" />
        <p>Create a custom list of recipients that will receive this report:</p>
        <CreatableSelect<RecipientOption, true>
          placeholder="Search users or enter an external email..."
          options={filteredOptions}
          isMulti
          inputValue={selectInputValue}
          onInputChange={(val) => setSelectInputValue(val)}
          value={[
            ...getDropdownOptions(Object.keys(selectedUserAccountIds)),
            ...externalRecipients.map((r) => ({
              label: r.emailAddress,
              value: r.emailAddress,
              name: r.emailAddress,
              isExternal: true as const,
            })),
          ]}
          onChange={handleSelectChange}
          onCreateOption={onCreateOption}
          formatOptionLabel={formatOptionLabel}
          formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
          isValidNewOption={(inputValue) => {
            const trimmed = inputValue.trim().toLowerCase();
            if (!isValidEmail(trimmed)) return false;
            return !Object.values(usersById).some(
              (u) => u.emailAddress.toLowerCase() === trimmed
            );
          }}
          filterOption={() => true}
          noOptionsMessage={() =>
            selectInputValue ? "No matching users found" : null
          }
          classNames={{
            multiValue: () => "multi-value user-option-white",
            option: () => "option-value-with-border-bottom option",
          }}
        />
        <div>{getAddOrRemoveAdminButton()}</div>
        <SimpleModalFooter
          onPrimaryButtonClick={() => {
            setShowConfirmationModal(true);
          }}
          primaryButtonText="Enable"
          onBackButtonClick={onHide}
          backButtonText="Back"
          isPrimaryButtonDisabled={
            Object.keys(selectedUserAccountIds).length === 0 &&
            externalRecipients.length === 0
          }
        />
      </SimpleModal>
    </>
  );
};

export default ConfigureWeeklyExecutiveCheckinReportModal;
